import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { thisBuild } from './build';
import { changeLine, isNewer, parseChanges, versionDetails, versionLabel, type AppBuild, type VersionChange } from './version';
import { leaveApp } from '../ui/unsavedCore';

type Changes = 'loading' | 'error' | VersionChange[];

async function fetchChanges(): Promise<VersionChange[] | null> {
  try {
    const res = await fetch('/api/version/changes', { cache: 'no-store', headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    return parseChanges(await res.json());
  } catch {
    return null;
  }
}

/**
 * Small „v102” next to the logo; the details (number, build time, commit) in the tooltip and after a click.
 * The details are placed `fixed` and kept inside the screen, so the sidebar or a phone's edge never clips them.
 *
 * `history` (inside the app, logged in — not on the login page): a click on the badge opens
 * „Historia zmian” (owner 2026-10-23 21:58: „nie wiem co poprawia ktora wersja”): the last versions, newest first,
 * „v127 · 23.10 21:31 · <tytuł>” with its „Nowości” points (docs/nowosci.md) under it, the running one marked; the list scrolls.
 * Only a click (owner 2026-10-25 23:27: „historie zmian laduj dopiero po kliknieciu”): hovering or focusing does nothing and
 * nothing is fetched before the first click; the list is kept after that. Closes on a second click, on Escape and on a click elsewhere.
 */
export function VersionBadge({ build = thisBuild, className, history = false }: { build?: AppBuild; className?: string; history?: boolean }) {
  const [open, setOpen] = useState(false);
  const [changes, setChanges] = useState<Changes | null>(null);
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  const details = versionDetails(build);

  useEffect(() => {
    if (!open || !history || changes !== null) return;
    setChanges('loading');
    void fetchChanges().then((list) => setChanges(list ?? 'error'));
  }, [open, history, changes]);

  useLayoutEffect(() => {
    if (!open) return;
    const r = button.current?.getBoundingClientRect();
    const w = pop.current?.offsetWidth ?? 0;
    const h = pop.current?.offsetHeight ?? 0;
    if (!r) return;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    // no room below (the login screen's footer): above the badge
    const top = r.bottom + 4 + h > window.innerHeight - 8 ? Math.max(8, r.top - 4 - h) : r.bottom + 4;
    setStyle({ position: 'fixed', top, left });
  }, [open, changes]);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !root.current?.contains(e.target as Node)) setOpen(false);
    };
    // fixed details would stay behind a scrolled badge; scrolling the history list itself keeps it
    const onScroll = (e: Event) => {
      if (!(e.target instanceof Node && pop.current?.contains(e.target))) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <span className={['version', className].filter(Boolean).join(' ')} ref={root}>
      <button
        ref={button}
        type="button"
        className="version-badge"
        title={history ? undefined : details}
        aria-label={details}
        aria-expanded={open}
        data-feature-id="hints.version"
        onClick={() => setOpen((o) => !o)}
        onBlur={history ? (e) => !root.current?.contains(e.relatedTarget as Node | null) && setOpen(false) : undefined}
      >
        {versionLabel(build)}
      </button>
      {open && !history && (
        <span className="version-details" ref={pop} style={style}>
          {details}
        </span>
      )}
      {open && history && (
        <span className="version-details version-history" ref={pop} style={style} role="dialog" aria-label="Historia zmian">
          <span className="version-history-head">
            <strong>Historia zmian</strong>
            <span>{details}</span>
          </span>
          {changes === 'loading' || changes === null ? (
            <span className="version-history-note">Wczytuję…</span>
          ) : changes === 'error' ? (
            <span className="version-history-note">Nie udało się pobrać historii.</span>
          ) : changes.length === 0 ? (
            <span className="version-history-note">Brak historii</span>
          ) : (
            <ul>
              {changes.map((c) => {
                const current = build.version === String(c.version);
                return (
                  <li key={c.version} aria-current={current ? 'true' : undefined} className={current ? 'current' : undefined}>
                    <span className="version-history-title">{changeLine(c)}</span>
                    {c.notes && (
                      <ul className="version-notes" aria-label={`Nowości w wersji ${c.version}`}>
                        {c.notes.map((n, i) => (
                          <li key={i}>
                            {n.text}
                            {n.items && (
                              <ul>
                                {n.items.map((t, j) => (
                                  <li key={j}>{t}</li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </span>
      )}
    </span>
  );
}

const CHECK_EVERY_MS = 5 * 60 * 1000;
/** Focus, visibilitychange and the timer together never ask more often than this. */
const MIN_GAP_MS = 30 * 1000;

const isField = (v: unknown): v is string | null => v === null || typeof v === 'string';

/** The body of GET /api/version, or null when it is not `{ version, commit, builtAt }` of strings / nulls. */
export function parseBuild(body: unknown): AppBuild | null {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return null;
  const { version, commit, builtAt } = body as Record<string, unknown>;
  return isField(version) && isField(commit) && isField(builtAt) ? { version, commit, builtAt } : null;
}

async function fetchServerBuild(): Promise<AppBuild | null> {
  try {
    const res = await fetch('/api/version', { cache: 'no-store', headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    return parseBuild(await res.json());
  } catch {
    return null; // offline, the server just restarting, not JSON: try again later
  }
}

/**
 * „Jest nowa wersja (v103) — odśwież” when the server already runs a newer build than this page: checked on load,
 * every 5 minutes and when the window comes back to the front — never while the tab is hidden, never two requests at
 * once, at most every 30 s, and no more once the notice is up. Not a modal: work in progress is never interrupted.
 */
export function NewVersionNotice({ build = thisBuild }: { build?: AppBuild }) {
  const [newer, setNewer] = useState<AppBuild | null>(null);
  useEffect(() => {
    if (newer) return; // found: nothing more to ask
    let alive = true;
    let busy = false;
    let last = -Infinity;
    const check = async () => {
      if (busy || document.visibilityState === 'hidden' || Date.now() - last < MIN_GAP_MS) return;
      busy = true;
      last = Date.now();
      try {
        const server = await fetchServerBuild();
        if (alive && server && isNewer(build, server)) setNewer(server);
      } finally {
        busy = false;
      }
    };
    const onEvent = () => void check();
    void check();
    const timer = setInterval(onEvent, CHECK_EVERY_MS);
    window.addEventListener('focus', onEvent);
    document.addEventListener('visibilitychange', onEvent);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('focus', onEvent);
      document.removeEventListener('visibilitychange', onEvent);
    };
  }, [build, newer]);
  if (!newer) return null;
  return (
    <div className="new-version" role="status">
      Jest nowa wersja ({versionLabel(newer)}) —{' '}
      <button type="button" className="link-button" onClick={() => leaveApp(() => window.location.reload())}>
        odśwież
      </button>
    </div>
  );
}
